import {io, Socket} from 'socket.io-client';
import {Preferences} from '@capacitor/preferences';
import {PreferenceKey} from '@/definitions/PreferenceKey';
import {SOCKET_URL} from '@/services/runtimeConfig';
import {Observable, ObservableEvent} from '@/services/observable/Observable';

let socket: Socket | null = null;

export type SendMessagePayload = {
    chatId?: number;
    peerUserId?: number;
    message?: string;
    feedId?: number;
    replyToId?: number;
    attachments?: Array<{
        data?: string;
        files?: string;
        name?: string;
        mimetype?: string;
        size?: number;
    }>;
};

async function token(): Promise<string | null> {
    return (await Preferences.get({key: PreferenceKey.ApiToken})).value;
}

export function getChatSocket(): Socket | null {
    return socket;
}

export async function connectChatSocket(): Promise<Socket | null> {
    const origin = SOCKET_URL;
    if (!origin) {
        console.warn('SOCKET_URL is not configured');
        return null;
    }

    const authToken = await token();
    if (!authToken) return null;

    if (socket?.connected) return socket;

    if (socket) {
        socket.disconnect();
        socket = null;
    }

    socket = io(origin, {
        transports: ['websocket', 'polling'],
        auth: {token: authToken},
        autoConnect: true,
        reconnection: true,
    });

    socket.on('new_message', (payload) => {
        Observable.emit(ObservableEvent.ChatsChanged, payload);
        Observable.emit(ObservableEvent.ChatMessageReceived, payload);
    });

    socket.on('message_reaction', (payload) => {
        Observable.emit(ObservableEvent.ChatMessageReceived, {type: 'reaction', ...payload});
    });

    socket.on('user_typing', (payload) => {
        Observable.emit(ObservableEvent.ChatTyping, payload);
    });

    socket.on('seen_update', (payload) => {
        Observable.emit(ObservableEvent.ChatSeen, payload);
    });

    socket.on('error_message', (payload) => {
        Observable.emit(ObservableEvent.ChatError, payload);
    });

    return socket;
}

export function disconnectChatSocket(): void {
    if (socket) {
        socket.disconnect();
        socket = null;
    }
}

export function joinChat(chatId: number): void {
    socket?.emit('join_chat', {chatId});
}

export function leaveChat(chatId: number): void {
    socket?.emit('leave_chat', {chatId});
}

export function sendChatMessage(payload: SendMessagePayload): Promise<any> {
    return new Promise((resolve, reject) => {
        if (!socket?.connected) {
            reject({error: 'Socket not connected'});
            return;
        }
        socket.emit('send_message', payload, (ack: any) => {
            if (ack?.error) {
                reject(ack);
                return;
            }
            resolve(ack);
        });
    });
}

export function emitTyping(chatId: number, isTyping: boolean): void {
    socket?.emit('typing', {chatId, isTyping});
}

export function emitMessageSeen(chatId: number, lastReadMessageId: number): void {
    socket?.emit('message_seen', {chatId, lastReadMessageId});
}

export function reactMessage(chatId: number, messageId: number, reaction: string): void {
    socket?.emit('react_message', {chatId, messageId, reaction});
}

export function unreactMessage(chatId: number, messageId: number, reaction?: string): void {
    socket?.emit('unreact_message', {chatId, messageId, reaction});
}
